import React, { useState, useEffect, useRef, useCallback } from 'react'
import { useOutletContext } from 'react-router-dom'
import { portalRequest } from '../../utils/portalApi'
import { useTeamPortalAuth } from '../../contexts/TeamPortalAuthContext'
import { 
  Send, MessageCircle, Users, Plus, ArrowLeft, Search, Image, X, Hash
} from 'lucide-react'

function timeAgo(dateStr) {
  if (!dateStr) return ''
  const d = new Date(dateStr)
  const now = new Date()
  const diff = Math.floor((now - d) / 1000)
  if (diff < 60) return 'just now'
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

function formatTime(dateStr) {
  if (!dateStr) return ''
  return new Date(dateStr).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
}

export default function PortalChat() {
  const { team, token } = useOutletContext()
  const { user } = useTeamPortalAuth()
  const [conversations, setConversations] = useState([])
  const [activeConvo, setActiveConvo] = useState(null)
  const [messages, setMessages] = useState([])
  const [newMessage, setNewMessage] = useState('')
  const [loading, setLoading] = useState(true)
  const [msgLoading, setMsgLoading] = useState(false)
  const [sending, setSending] = useState(false)
  const [showSidebar, setShowSidebar] = useState(true)
  const [showNewConvo, setShowNewConvo] = useState(false)
  const [newConvoName, setNewConvoName] = useState('')
  const [creating, setCreating] = useState(false)
  const messagesEndRef = useRef(null)
  const pollRef = useRef(null)

  useEffect(() => {
    fetchConversations()
    return () => { if (pollRef.current) clearInterval(pollRef.current) }
  }, [])

  useEffect(() => {
    if (activeConvo) {
      fetchMessages(activeConvo)
      // Mark as read
      portalRequest(`/api/chat/conversations/${activeConvo}/read`, { method: 'POST' }).catch(() => {})
      // Poll for new messages every 5s
      if (pollRef.current) clearInterval(pollRef.current)
      pollRef.current = setInterval(() => fetchMessages(activeConvo, true), 5000)
    }
    return () => { if (pollRef.current) clearInterval(pollRef.current) }
  }, [activeConvo])

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  const fetchConversations = async () => {
    try {
      const data = await portalRequest('/api/chat/conversations')
      setConversations(Array.isArray(data) ? data : data?.conversations || [])
      // Auto-select first if none selected
      if (!activeConvo && data?.length > 0) {
        const id = data[0].conversation_id || data[0]._id
        setActiveConvo(id)
      }
    } catch (err) {
      console.error('Error fetching conversations:', err)
    } finally {
      setLoading(false)
    }
  }

  const fetchMessages = async (convoId, silent = false) => {
    if (!silent) setMsgLoading(true)
    try {
      const data = await portalRequest(`/api/chat/conversations/${convoId}/messages?limit=50`)
      const msgs = Array.isArray(data) ? data : data?.messages || []
      setMessages(msgs)
    } catch (err) {
      console.error('Error fetching messages:', err)
    } finally {
      if (!silent) setMsgLoading(false)
    }
  }

  const handleSend = async (e) => {
    e.preventDefault()
    if (!newMessage.trim() || !activeConvo || sending) return

    setSending(true)
    const text = newMessage.trim()
    setNewMessage('')

    // Optimistic update
    const optimistic = {
      _id: `temp-${Date.now()}`,
      content: text,
      sender_id: user?._id || user?.user_id,
      sender_name: user?.name,
      created_at: new Date().toISOString(),
      type: 'text',
    }
    setMessages(prev => [...prev, optimistic])

    try {
      await portalRequest(`/api/chat/conversations/${activeConvo}/messages`, {
        method: 'POST',
        body: JSON.stringify({ content: text, type: 'text' })
      })
      fetchMessages(activeConvo, true)
    } catch (err) {
      // Remove optimistic message on error
      setMessages(prev => prev.filter(m => m._id !== optimistic._id))
      setNewMessage(text)
    } finally {
      setSending(false)
    }
  }

  const createConversation = async () => {
    if (!newConvoName.trim()) return
    setCreating(true)
    try {
      const data = await portalRequest('/api/chat/conversations', {
        method: 'POST',
        body: JSON.stringify({ 
          name: newConvoName.trim(),
          team_id: team.team_id,
          type: 'group'
        })
      })
      const id = data.conversation_id || data._id
      setShowNewConvo(false)
      setNewConvoName('')
      await fetchConversations()
      setActiveConvo(id)
    } catch (err) {
      console.error('Error creating conversation:', err)
    } finally {
      setCreating(false)
    }
  }

  const activeConvoData = conversations.find(c => (c.conversation_id || c._id) === activeConvo)
  const userId = user?._id || user?.user_id

  return (
    <div className="flex h-[calc(100vh-8rem)] -m-6 lg:-m-8 bg-zinc-900 rounded-2xl overflow-hidden border border-zinc-700">
      {/* Sidebar */}
      <div className={`${showSidebar ? 'flex' : 'hidden'} lg:flex flex-col w-full lg:w-80 border-r border-zinc-700 bg-zinc-800`}>
        <div className="p-4 border-b border-zinc-700">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-bold text-white">Messages</h2>
            <button
              onClick={() => setShowNewConvo(!showNewConvo)}
              className="p-2 rounded-lg hover:bg-zinc-700 text-zinc-400 hover:text-white transition-colors"
            >
              <Plus className="w-5 h-5" />
            </button>
          </div>

          {showNewConvo && (
            <div className="flex gap-2 mb-3">
              <input
                type="text"
                value={newConvoName}
                onChange={(e) => setNewConvoName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && createConversation()}
                className="flex-1 px-3 py-2 bg-zinc-900 border border-zinc-700 rounded-lg text-white text-sm placeholder-zinc-500 focus:ring-2 focus:ring-green-500 outline-none"
                placeholder="Group name..."
                autoFocus
              />
              <button
                onClick={createConversation}
                disabled={creating}
                className="px-3 py-2 bg-green-500 text-white rounded-lg text-sm font-medium hover:bg-green-600 disabled:opacity-50"
              >
                {creating ? '...' : 'Create'}
              </button>
            </div>
          )}
        </div>

        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-500"></div>
            </div>
          ) : conversations.length === 0 ? (
            <div className="text-center py-8 px-4">
              <MessageCircle className="w-10 h-10 text-zinc-600 mx-auto mb-2" />
              <p className="text-sm text-zinc-500">No conversations yet</p>
            </div>
          ) : (
            conversations.map(convo => {
              const id = convo.conversation_id || convo._id
              const isActive = id === activeConvo
              return (
                <button
                  key={id}
                  onClick={() => { setActiveConvo(id); setShowSidebar(false) }}
                  className={`w-full flex items-center gap-3 p-4 text-left transition-colors ${
                    isActive ? 'bg-zinc-700/50' : 'hover:bg-zinc-700/30'
                  }`}
                >
                  <div className="w-10 h-10 rounded-full bg-zinc-700 flex items-center justify-center flex-shrink-0">
                    {convo.type === 'dm' ? (
                      <Users className="w-5 h-5 text-zinc-400" />
                    ) : (
                      <Hash className="w-5 h-5 text-zinc-400" />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium text-white truncate">
                        {convo.name || 'Direct Message'}
                      </p>
                      <span className="text-xs text-zinc-500 flex-shrink-0 ml-2">
                        {timeAgo(convo.last_message_at || convo.updated_at)}
                      </span>
                    </div>
                    {convo.last_message && (
                      <p className="text-xs text-zinc-500 truncate mt-0.5">
                        {convo.last_message}
                      </p>
                    )}
                  </div>
                  {convo.unread_count > 0 && (
                    <span className="w-5 h-5 rounded-full bg-green-500 text-white text-xs flex items-center justify-center font-bold flex-shrink-0">
                      {convo.unread_count}
                    </span>
                  )}
                </button>
              )
            })
          )}
        </div>
      </div>

      {/* Chat Area */}
      <div className={`${!showSidebar || activeConvo ? 'flex' : 'hidden'} lg:flex flex-col flex-1`}>
        {activeConvo ? (
          <>
            {/* Chat Header */}
            <div className="flex items-center gap-3 p-4 border-b border-zinc-700 bg-zinc-800">
              <button
                onClick={() => setShowSidebar(true)}
                className="lg:hidden p-1 rounded hover:bg-zinc-700 text-zinc-400"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="w-8 h-8 rounded-full bg-zinc-700 flex items-center justify-center">
                <Hash className="w-4 h-4 text-zinc-400" />
              </div>
              <div>
                <p className="text-sm font-semibold text-white">
                  {activeConvoData?.name || 'Conversation'}
                </p>
                {activeConvoData?.member_count && (
                  <p className="text-xs text-zinc-500">{activeConvoData.member_count} members</p>
                )}
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {msgLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-500"></div>
                </div>
              ) : messages.length === 0 ? (
                <div className="text-center py-8">
                  <MessageCircle className="w-10 h-10 text-zinc-700 mx-auto mb-2" />
                  <p className="text-sm text-zinc-500">No messages yet. Start the conversation!</p>
                </div>
              ) : (
                messages.map((msg, i) => {
                  const isMine = msg.sender_id === userId
                  const showAvatar = i === 0 || messages[i-1]?.sender_id !== msg.sender_id
                  return (
                    <div key={msg._id || i} className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[75%] ${isMine ? 'items-end' : 'items-start'}`}>
                        {showAvatar && !isMine && (
                          <p className="text-xs text-zinc-500 mb-1 ml-1">{msg.sender_name || 'Unknown'}</p>
                        )}
                        <div className={`px-4 py-2.5 rounded-2xl ${
                          isMine 
                            ? 'bg-green-500 text-white rounded-br-md' 
                            : 'bg-zinc-700 text-zinc-200 rounded-bl-md'
                        }`}>
                          <p className="text-sm whitespace-pre-wrap break-words">{msg.content}</p>
                        </div>
                        <p className={`text-xs text-zinc-600 mt-1 ${isMine ? 'text-right mr-1' : 'ml-1'}`}>
                          {formatTime(msg.created_at)}
                        </p>
                      </div>
                    </div>
                  )
                })
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <form onSubmit={handleSend} className="p-4 border-t border-zinc-700 bg-zinc-800">
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  className="flex-1 px-4 py-3 bg-zinc-900 border border-zinc-700 rounded-xl text-white placeholder-zinc-500 focus:ring-2 focus:ring-green-500 focus:border-green-500 outline-none"
                  placeholder="Type a message..."
                />
                <button
                  type="submit"
                  disabled={!newMessage.trim() || sending}
                  className="p-3 bg-green-500 text-white rounded-xl hover:bg-green-600 disabled:opacity-50 disabled:hover:bg-green-500 transition-colors"
                >
                  <Send className="w-5 h-5" />
                </button>
              </div>
            </form>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <MessageCircle className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
              <p className="text-zinc-500">Select a conversation to start messaging</p>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
